import React, { useEffect, useState } from "react";
import showToast from "../../utils/toastUtils";
import { useNavigate } from "react-router-dom";
import { FaCopy } from "react-icons/fa";
import { BiLoaderCircle } from "react-icons/bi";
import { getPartner, updatePartner } from "../../data/api/api";
import Loader from "../../components/Loader/Loader";
import { ThemeContext } from "../../themes/ThemeContext";
import { useTestPartner } from "../../hooks/useTestPartner";
import { TestAccountBanner } from "../../components";

function Settings() {
  const theme = React.useContext(ThemeContext);
  const navigate = useNavigate();
  const { isTestPartner } = useTestPartner();
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    webhookUrl: "",
    app_key: "",
    acctName: "",
    acctNumber: "",
    businessName: "",
    partnerId: "",
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [isLoading, setIsLoading] = useState({
    accData: false,
    webhook: false,
    password: false,
  });

  useEffect(() => {
    let isMounted = true;
    setLoadingUserData(true);

    const fetchData = async () => {
      try {
        const pid = localStorage.getItem("pid");
        const partnerResponse = await getPartner(pid);

        if (isMounted && partnerResponse.status === "success") {
          const partnerData = partnerResponse.data;
          setFormData({
            email: partnerData.email,
            firstName: partnerData.first_name,
            lastName: partnerData.last_name,
            webhookUrl: partnerData.webhookUrl || "",
            app_key: partnerData.app_key || "",
            acctName: partnerData.acctName || "",
            acctNumber: partnerData.acctNumber || "",
            businessName: partnerData.business_name || "",
            partnerId: partnerData.partnerId || "",
          });
          // localStorage.setItem("userFirstName", partnerData.first_name);
          // localStorage.setItem("acctNumber", partnerData.acctNumber);
          setLoadingUserData(false);
        }
      } catch (error) {
        if (isMounted) {
          setLoadingUserData(false);
          // console.error("Error fetching partner data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const validatePasswordUpdate = () => {
    const errors = {};

    // Validate current password
    if (!formData.currentPassword.trim()) {
      errors.currentPassword = "Current password is required.";
    }

    // Validate new password
    if (!formData.newPassword.trim()) {
      errors.newPassword = "New password is required.";
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = "New password must be at least 8 characters long.";
    }

    // Validate confirm new password
    if (!formData.confirmNewPassword.trim()) {
      errors.confirmNewPassword = "Please confirm your new password.";
    } else if (formData.confirmNewPassword !== formData.newPassword) {
      errors.confirmNewPassword = "Passwords do not match.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleSave = async () => {
    if (isTestPartner) {
      showToast("error", "Partner information cannot be modified in test mode.", 3000);
      return;
    }

    setIsLoading({ ...isLoading, accData: true });
    try {
      const pid = localStorage.getItem("pid");
      const updatedData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        webhookUrl: formData.webhookUrl,
      };

      const updateResponse = await updatePartner(pid, updatedData);

      if (updateResponse.status === "success") {
        showToast("success", updateResponse.message, 3000);
        localStorage.setItem("userFirstName", formData.firstName);
      } else {
        showToast("error", updateResponse.message || "Couldn't update account details", 3000);
      }
    } catch (error) {
      showToast("error", "Couldn't update account details", 3000);
    } finally {
      setIsLoading({ ...isLoading, accData: false });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast("success", "Copied to clipboard", 3000);
  };

  const handleTerminateAccount = () => {
    showToast("info", "Account termination feature not implemented yet", 3000);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/sign-in");
  };

  const handleGenerateAccount = () => {
    showToast("info", "Generate account feature not implemented yet", 3000);
  };

  const handleUpdatePassword = async () => {
    if (isTestPartner) {
      showToast("error", "Password cannot be modified in test mode.", 3000);
      return;
    }

    if (!validatePasswordUpdate()) return;

    setIsLoading({ ...isLoading, password: true });

    try {
      const pid = localStorage.getItem("pid");
      const updateResponse = await updatePartner(pid, {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      if (updateResponse.status === "success") {
        showToast("success", "Password updated successfully", 3000);
        setFormData({
          ...formData,
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        });
        setValidationErrors({});
      } else {
        showToast("error", updateResponse.message || "Failed to update password", 3000);
      }
    } catch (error) {
      showToast("error", "Failed to update password", 3000);
    } finally {
      setIsLoading({ ...isLoading, password: false });
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 rounded-md overflow-hidden min-h-screen">
      <TestAccountBanner />
      <div style={{ color: theme.colors.text.primary }}>
        <div className="pageTitle text-3xl font-semibold">Profile Settings</div>
        <div className="tag text-md mt-2 text-xs font-thin">
          Update your Profile Details
        </div>
      </div>

      {loadingUserData ? (
        <Loader message={"Loading your account info. Hold on"} />
      ) : (
        <div>
          <div className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">First Name</div>
                <input
                  className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                    validationErrors.firstName ? "border-red-500" : "border-gray-200"
                  } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="text"
                  placeholder="John"
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                  disabled={isTestPartner}
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.accent}`,
                    color: theme.colors.text.primary,
                    opacity: isTestPartner ? 0.6 : 1,
                    cursor: isTestPartner ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Last Name</div>
                <input
                  className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                    validationErrors.lastName ? "border-red-500" : "border-gray-200"
                  } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="text"
                  placeholder="Doe"
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                  disabled={isTestPartner}
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.accent}`,
                    color: theme.colors.text.primary,
                    opacity: isTestPartner ? 0.6 : 1,
                    cursor: isTestPartner ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Email</div>
                <input
                  className={`w-full px-8 py-4 rounded-full cursor-not-allowed font-medium bg-white border border-gray-200 placeholder-gray-500 text-gray-400 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="email"
                  value={formData.email}
                  readOnly
                  placeholder="youremail@email.com"
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.grey}`,
                    color: theme.colors.text.grey,
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Webhook URL</div>
                <input
                  className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                    validationErrors.webhookUrl ? "border-red-500" : "border-gray-200"
                  } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="text"
                  placeholder="https://yourwebhook.com"
                  value={formData.webhookUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, webhookUrl: e.target.value })
                  }
                  disabled={isTestPartner}
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.accent}`,
                    color: theme.colors.text.primary,
                    opacity: isTestPartner ? 0.6 : 1,
                    cursor: isTestPartner ? "not-allowed" : "text",
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Account Name</div>
                <div className="flex items-center gap-2">
                  <input
                    className={`w-full px-8 py-4 rounded-full cursor-not-allowed font-medium bg-white border border-gray-200 placeholder-gray-500 text-gray-400 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                    type="text"
                    value={formData.acctName}
                    readOnly
                    style={{
                      backgroundColor: theme.colors.background.primary,
                      border: `1px solid ${theme.colors.background.grey}`,
                      color: theme.colors.text.grey,
                    }}
                  />
                  <button
                    className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                    onClick={() => copyToClipboard(formData.acctName)}
                    style={{
                      background:
                        "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%), linear-gradient(0deg, rgba(0, 0, 21, 0.1), rgba(0, 0, 21, 0.1))",
                      color: "white",
                    }}
                  >
                    <FaCopy className="mr-2" />
                    Copy
                  </button>
                </div>
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Account Number</div>
                <div className="flex items-center gap-2">
                  <input
                    className={`w-full px-8 py-4 rounded-full cursor-not-allowed font-medium bg-white border border-gray-200 placeholder-gray-500 text-gray-400 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                    type="text"
                    value={formData.acctNumber}
                    readOnly
                    style={{
                      backgroundColor: theme.colors.background.primary,
                      border: `1px solid ${theme.colors.background.grey}`,
                      color: theme.colors.text.grey,
                    }}
                  />
                  <button
                    className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                    onClick={() => copyToClipboard(formData.acctNumber)}
                    style={{
                      background:
                        "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%), linear-gradient(0deg, rgba(0, 0, 21, 0.1), rgba(0, 0, 21, 0.1))",
                      color: "white",
                    }}
                  >
                    <FaCopy className="mr-2" />
                    Copy
                  </button>
                </div>
                {!formData.acctNumber && (
                  <button
                    className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer mt-2"
                    onClick={handleGenerateAccount}
                    style={{
                      background: "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%)",
                      color: "white",
                    }}
                  >
                    Generate your account
                  </button>
                )}
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Business Name</div>
                <input
                  className={`w-full px-8 py-4 rounded-full cursor-not-allowed font-medium bg-white border border-gray-200 placeholder-gray-500 text-gray-400 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="text"
                  value={formData.businessName}
                  readOnly
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.grey}`,
                    color: theme.colors.text.grey,
                  }}
                />
              </div>

              <div className="flex flex-col">
                <div className="text-xs mb-1 ml-1 text-gray-400">Partner ID</div>
                <input
                  className={`w-full px-8 py-4 rounded-full cursor-not-allowed font-medium bg-white border border-gray-200 placeholder-gray-500 text-gray-400 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                  type="text"
                  value={formData.partnerId}
                  readOnly
                  style={{
                    backgroundColor: theme.colors.background.primary,
                    border: `1px solid ${theme.colors.background.grey}`,
                    color: theme.colors.text.grey,
                  }}
                />
              </div>
            </div>

            <div className="mt-6">
              {isTestPartner ? (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <p>Partner information cannot be modified in test mode.</p>
                  <p className="text-xs mt-2">Contact your administrator for changes.</p>
                </div>
              ) : (
                <button
                  className={`text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer`}
                  onClick={handleSave}
                  disabled={isLoading.accData}
                  style={{
                    background:
                      "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%), linear-gradient(0deg, rgba(0, 0, 21, 0.1), rgba(0, 0, 21, 0.1))",
                    color: "white",
                    opacity: isLoading.accData ? 0.6 : 1,
                    cursor: isLoading.accData ? "not-allowed" : "pointer",
                  }}
                >
                  Save Account Details
                  {isLoading.accData && (
                    <BiLoaderCircle className="ml-2 animate-spin" color="#9999FF" />
                  )}
                </button>
              )}
            </div>
          </div>

<br />
          
          <div className="mt-10">
                <div className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
                  Update Password
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div className="flex flex-col">
                    <div className="text-xs mb-1 ml-1 text-gray-400">Current Password</div>
                    <input
                      className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                        validationErrors.currentPassword ? "border-red-500" : "border-gray-200"
                      } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                      type="password"
                      placeholder="Enter your current password"
                      value={formData.currentPassword || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, currentPassword: e.target.value })
                      }
                      disabled={isTestPartner}
                      style={{
                        backgroundColor: theme.colors.background.primary,
                        border: `1px solid ${theme.colors.background.accent}`,
                        color: theme.colors.text.primary,
                        opacity: isTestPartner ? 0.6 : 1,
                        cursor: isTestPartner ? "not-allowed" : "text",
                      }}
                    />
                    {validationErrors.currentPassword && (
                      <div className="text-red-500 text-xs mt-1">
                        {validationErrors.currentPassword}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="text-xs mb-1 ml-1 text-gray-400">New Password</div>
                    <input
                      className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                        validationErrors.newPassword ? "border-red-500" : "border-gray-200"
                      } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                      type="password"
                      placeholder="Enter your new password"
                      value={formData.newPassword || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, newPassword: e.target.value })
                      }
                      disabled={isTestPartner}
                      style={{
                        backgroundColor: theme.colors.background.primary,
                        border: `1px solid ${theme.colors.background.accent}`,
                        color: theme.colors.text.primary,
                        opacity: isTestPartner ? 0.6 : 1,
                        cursor: isTestPartner ? "not-allowed" : "text",
                      }}
                    />
                    {validationErrors.newPassword && (
                      <div className="text-red-500 text-xs mt-1">
                        {validationErrors.newPassword}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col">
                    <div className="text-xs mb-1 ml-1 text-gray-400">Confirm New Password</div>
                    <input
                      className={`w-full px-8 py-4 rounded-full font-medium bg-white border ${
                        validationErrors.confirmNewPassword ? "border-red-500" : "border-gray-200"
                      } placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white`}
                      type="password"
                      placeholder="Confirm your new password"
                      value={formData.confirmNewPassword || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, confirmNewPassword: e.target.value })
                      }
                      disabled={isTestPartner}
                      style={{
                        backgroundColor: theme.colors.background.primary,
                        border: `1px solid ${theme.colors.background.accent}`,
                        color: theme.colors.text.primary,
                        opacity: isTestPartner ? 0.6 : 1,
                        cursor: isTestPartner ? "not-allowed" : "text",
                      }}
                    />
                    {validationErrors.confirmNewPassword && (
                      <div className="text-red-500 text-xs mt-1">
                        {validationErrors.confirmNewPassword}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  {isTestPartner ? (
                    <div className="text-center py-4 text-gray-500 text-sm">
                      <p>Password cannot be modified in test mode.</p>
                    </div>
                  ) : (
                    <button
                      className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                      onClick={handleUpdatePassword}
                      disabled={isLoading.password}
                      style={{
                        background: "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%)",
                        color: "white",
                        opacity: isLoading.password ? 0.6 : 1,
                        cursor: isLoading.password ? "not-allowed" : "pointer",
                      }}
                    >
                      Update Password
                      {isLoading.password && (
                        <BiLoaderCircle className="ml-2 animate-spin" color="#9999FF" />
                      )}
                    </button>
                  )}
                </div>
              </div>

          <div className="mt-10">
            <div className="text-xl font-semibold" style={{ color: theme.colors.text.primary }}>
              App Key
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                className="w-1/2 px-8 py-4 rounded-full font-medium bg-white border border-gray-200 placeholder-gray-500 text-xs focus:outline-none focus:border-gray-400 focus:bg-white"
                type="text"
                value={formData.app_key}
                readOnly
                style={{
                  backgroundColor: theme.colors.background.primary,
                  border: `1px solid ${theme.colors.background.accent}`,
                  color: theme.colors.background.accent,
                }}
              />
              <button
                className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                onClick={() => copyToClipboard(formData.app_key)}
                style={{
                  background:
                    "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%), linear-gradient(0deg, rgba(0, 0, 21, 0.1), rgba(0, 0, 21, 0.1))",
                  color: "white",
                }}
              >
                <FaCopy className="mr-2" />
                Copy Key
              </button>
            </div>
          </div>

          <div className="mt-10">
            <div className="text-xl font-semibold text-white">Account Actions</div>
            <div className="flex items-center gap-4 mt-4">
              <button
                className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                onClick={handleTerminateAccount}
                style={{
                  background: "linear-gradient(270deg, #FD2E2E 0.05%, #9999FF 99.97%)",
                  color: "white",
                }}
              >
                Terminate Account
              </button>
              <button
                className="text-white rounded-full px-4 py-2 text-xs flex items-center cursor-pointer"
                onClick={handleLogout}
                style={{
                  background: "linear-gradient(270deg, #4B49AC 0.05%, #9999FF 99.97%)",
                  color: "white",
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Settings;